/**
 * Create your Railit account.
 *
 * Two doors and nothing else. The old page asked for a company name, your name,
 * an email and a password before it would let you in — four fields and a
 * decision (what is this company called?) at the moment a visitor has the least
 * patience. Signing up is now one click, and the company is built afterwards in
 * the wizard, where there is room to explain it.
 *
 * An account here has NO organization yet. That is deliberate and it is what
 * makes "Continue with Google" possible: Google hands us an identity, never a
 * company. See lib/onboarding.ts for the credential a half-finished account
 * holds, and pages/Onboarding.tsx for what it does with it.
 */

import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { Alert, AlertDescription } from '@/components/shadcn/alert';
import { Button } from '@/components/shadcn/button';
import { Logo } from '@/components/ui/Logo';
import { GoogleMark } from '@/components/ui/GoogleMark';

/** What the server will actually let us offer. */
interface HealthResponse {
  features?: { google?: boolean };
}

const COPY = {
  en: {
    title: 'Create your workspace',
    lede: 'Your whole company in one system — books, people, and an AI that can read them.',
    google: 'Continue with Google',
    email: 'Continue with email',
    terms: 'By signing up, you agree to our',
    tos: 'Terms of Service',
    and: 'and',
    privacy: 'Privacy Policy',
    have: 'Already have an account?',
    login: 'Log in',
    cancelled: 'Google sign-in was cancelled.',
    failed: 'Google sign-in did not complete. Try again, or use email.',
  },
  ar: {
    title: 'أنشئ مساحة عملك',
    lede: 'شركتك كاملة في نظام واحد — الحسابات والفريق وذكاء اصطناعي يقرأها.',
    google: 'المتابعة بحساب Google',
    email: 'المتابعة بالبريد الإلكتروني',
    terms: 'بإنشائك حسابًا فإنك توافق على',
    tos: 'شروط الخدمة',
    and: 'و',
    privacy: 'سياسة الخصوصية',
    have: 'لديك حساب بالفعل؟',
    login: 'تسجيل الدخول',
    cancelled: 'تم إلغاء تسجيل الدخول عبر Google.',
    failed: 'لم يكتمل تسجيل الدخول عبر Google. حاول مجددًا أو استخدم البريد.',
  },
} as const;

export function Signup() {
  const { lang } = useI18n();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [leaving, setLeaving] = useState(false);
  const c = COPY[lang === 'ar' ? 'ar' : 'en'];

  // Whether Google is configured is a server fact, not a build-time one — the
  // button must not appear on a deployment that would fail at the token
  // exchange, after the customer has already left the app.
  const { data: health } = useQuery<HealthResponse>({
    queryKey: ['health', 'features'],
    queryFn: async () => (await fetch('/api/health')).json(),
    staleTime: 5 * 60_000,
  });
  const googleReady = health?.features?.google === true;

  const googleError = params.get('google');

  useEffect(() => {
    // Coming back from a cancelled Google hop leaves a query string behind.
    if (googleError) {
      const url = new URL(window.location.href);
      url.searchParams.delete('google');
      window.history.replaceState({}, '', url.toString());
    }
  }, [googleError]);

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const startGoogle = () => {
    setLeaving(true);
    // A full navigation, not fetch: this is an OAuth hop and the browser has to
    // own it — Google will not answer a cross-origin XHR.
    window.location.href = '/api/erp/auth/google?next=/app/dashboard';
  };

  return (
    <div className="flex min-h-svh flex-col items-center justify-center px-6 py-16">
      <div className="w-full max-w-[360px]">
        <div className="flex flex-col items-center text-center">
          <Logo size={32} markOnly />
          <h1 className="mt-6 text-2xl font-semibold tracking-tight">{c.title}</h1>
          <p className="mt-2 text-sm leading-relaxed text-balance text-muted-foreground">
            {c.lede}
          </p>
        </div>

        {googleError ? (
          <Alert variant="destructive" role="alert" className="mt-6">
            <AlertDescription>
              {googleError === 'cancelled' ? c.cancelled : c.failed}
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="mt-8 grid gap-3">
          {googleReady ? (
            <Button
              type="button"
              size="lg"
              className="h-11 w-full"
              onClick={startGoogle}
              disabled={leaving}
            >
              {leaving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <GoogleMark className="size-4" />
              )}
              {c.google}
            </Button>
          ) : null}

          <Button
            type="button"
            variant={googleReady ? 'outline' : 'default'}
            size="lg"
            className="h-11 w-full"
            onClick={() => navigate('/signup/email')}
          >
            <Mail className="size-4" />
            {c.email}
          </Button>
        </div>

        <p className="mt-6 text-center text-xs leading-relaxed text-muted-foreground">
          {c.terms}{' '}
          <a href="/terms" className="underline underline-offset-2 hover:text-foreground">
            {c.tos}
          </a>{' '}
          {c.and}{' '}
          <a href="/privacy" className="underline underline-offset-2 hover:text-foreground">
            {c.privacy}
          </a>
          .
        </p>

        <p className="mt-8 text-center text-sm text-muted-foreground">
          {c.have}{' '}
          <Link to="/login" className="font-medium text-foreground hover:underline">
            {c.login}
          </Link>
        </p>
      </div>
    </div>
  );
}
